import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import projectService from '../../services/projectService';
import inspectionService from '../../services/inspectionService';
import DatePickerField from '../../components/common/DatePickerField';
import { useAuth } from '../../contexts/AuthContext';
import { TRACKING_CATEGORIES, DEFAULT_TRACKING_CATEGORIES } from '../../constants/trackingCategories';
import './Projects.css';

const RAG_CLASS = { GREEN: 'success', AMBER: 'warning', RED: 'danger' };

const Projects = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { organisation } = useAuth();
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [scoresByProject, setScoresByProject] = useState({});

  // Fetch projects from API
  const fetchProjects = async () => {
    try {
      setLoading(true);
      const data = await projectService.getAll();
      // Map API response to frontend format with default values for fields that might not exist yet
      const mappedProjects = (data.projects || []).map(p => ({
        ...p,
        risks: p.risks || { high: 0, medium: 0, low: 0 },
        standards: p.standards || [],
        workers: p.numberOfWorkers ?? null,
        lastInspection: p.lastInspection || null,
      }));
      setProjects(mappedProjects);

      // Real severity-weighted compliance score per project (see
      // ComplianceService.calculateProjectFindingsScore on the backend) -
      // fetched per project since there's no bulk endpoint yet, same pattern
      // as Reports.js.
      const entries = await Promise.all(mappedProjects.map(async (p) => {
        try {
          const score = await inspectionService.getScore({ projectId: p.id });
          return [p.id, score];
        } catch (error) {
          console.error(`Failed to load compliance score for project ${p.id}:`, error);
          return [p.id, null];
        }
      }));
      setScoresByProject(Object.fromEntries(entries));
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  // Open create modal if navigated with state
  useEffect(() => {
    if (location.state?.openCreateModal) {
      setShowCreateModal(true);
      // Clear the state so it doesn't reopen on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Handle project creation
  const handleCreateProject = async (projectData) => {
    try {
      setCreating(true);
      await projectService.create(projectData);
      toast.success('Project created successfully!');
      setShowCreateModal(false);
      fetchProjects(); // Refresh the list
    } catch (error) {
      console.error('Failed to create project:', error);
      toast.error(error.response?.data?.error || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  // Handle project update
  const handleUpdateProject = async (projectData) => {
    try {
      setUpdating(true);
      await projectService.update(editingProject.id, {
        name: projectData.name,
        code: projectData.code,
        description: projectData.description,
        projectStage: projectData.projectStage,
        projectType: projectData.projectType,
        projectTypeOther: projectData.projectTypeOther,
        location: projectData.location,
        latitude: projectData.latitude ? parseFloat(projectData.latitude) : null,
        longitude: projectData.longitude ? parseFloat(projectData.longitude) : null,
        countryCode: projectData.country,
        startDate: projectData.startDate || null,
        endDate: projectData.endDate || null,
        contractor: projectData.contractor,
        projectManager: projectData.projectManager,
        trackingCategories: projectData.trackingCategories,
        customCategories: projectData.customCategories,
        numberOfWorkers: projectData.numberOfWorkers === '' || projectData.numberOfWorkers === undefined
          ? null : Number(projectData.numberOfWorkers),
        locations: projectData.locations || [],
      });
      toast.success('Project updated successfully!');
      setShowEditModal(false);
      setEditingProject(null);
      fetchProjects(); // Refresh the list
    } catch (error) {
      console.error('Failed to update project:', error);
      toast.error(error.response?.data?.error || 'Failed to update project');
    } finally {
      setUpdating(false);
    }
  };

  // Open edit modal
  const openEditModal = (project) => {
    setEditingProject(project);
    setShowEditModal(true);
  };


  // Statistics
  const scoresLoaded = Object.values(scoresByProject).filter(Boolean);
  const trackWorkers = organisation?.hasWorkersOnSite !== false;
  const stats = {
    total: projects.length,
    active: projects.filter(p => p.status === 'active').length,
    running: projects.filter(p => p.status === 'running').length,
    completed: projects.filter(p => p.status === 'completed').length,
    onHold: projects.filter(p => p.status === 'on-hold').length,
    totalWorkers: projects.reduce((sum, p) => sum + (p.workers || 0), 0),
    highRisks: projects.reduce((sum, p) => sum + p.risks.high, 0),
    avgCompliance: scoresLoaded.length > 0
      ? Math.round((scoresLoaded.reduce((sum, s) => sum + s.score, 0) / scoresLoaded.length) * 100)
      : null,
  };

  // Count of actively monitored projects (active + running)
  const activelyMonitored = stats.active + stats.running;

  const getStatusBadge = (status) => {
    const badges = {
      'active': { className: 'status-success', label: 'Active', icon: 'fa-check-circle' },
      'running': { className: 'status-running', label: 'Running', icon: 'fa-play-circle' },
      'completed': { className: 'status-completed', label: 'Completed', icon: 'fa-flag-checkered' },
      'on-hold': { className: 'status-warning', label: 'On Hold', icon: 'fa-pause-circle' }
    };
    const badge = badges[status] || badges['active'];
    return (
      <span className={`project-status-badge ${badge.className}`}>
        <i className={`fas ${badge.icon}`}></i> {badge.label}
      </span>
    );
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (project.code || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (project.location || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || project.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="projects-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#16a34a' }}></i>
          <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="projects-page">
      {/* Header */}
      <div className="projects-header">
        <div className="header-content">
          <div className="header-title">
            <h1><i className="fas fa-project-diagram"></i> Projects</h1>
            <p className="header-subtitle">Manage and monitor all your compliance across projects</p>
          </div>
          <button 
            className="btn-primary-green" 
            onClick={() => setShowCreateModal(true)}
          >
            <i className="fas fa-plus"></i> New Project
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon">
            <i className="fas fa-folder-open"></i>
          </div>
          <div className="stat-details">
            <span className="stat-label">Total Projects</span>
            <span className="stat-value">{stats.total}</span>
            <span className="stat-trend">
              <i className="fas fa-circle" style={{ fontSize: '8px', color: '#16a34a' }}></i> {activelyMonitored} in progress
            </span>
          </div>
        </div>

        <div className="stat-card success">
          <div className="stat-icon">
            <i className="fas fa-check-circle"></i>
          </div>
          <div className="stat-details">
            <span className="stat-label">Avg Compliance</span>
            <span className="stat-value">{stats.avgCompliance !== null ? `${stats.avgCompliance}%` : 'N/A'}</span>
            <span className="stat-trend">Blended from latest inspections + findings resolution</span>
          </div>
        </div>

        {trackWorkers && (
          <div className="stat-card info">
            <div className="stat-icon">
              <i className="fas fa-users"></i>
            </div>
            <div className="stat-details">
              <span className="stat-label">Total Workers</span>
              <span className="stat-value">{stats.totalWorkers.toLocaleString()}</span>
              <span className="stat-trend">Across all sites</span>
            </div>
          </div>
        )}

        <div className="stat-card warning">
          <div className="stat-icon">
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <div className="stat-details">
            <span className="stat-label">High Risks</span>
            <span className="stat-value">{stats.highRisks}</span>
            <span className="stat-trend">
              {stats.highRisks > 0 ? 'Requires attention' : 'All under control'}
            </span>
          </div>
        </div>
      </div>

      {/* Filters & Controls */}
      <div className="projects-controls">
        {/* Search Bar - only show if more than 2 projects */}
        {projects.length > 2 && (
          <div className="search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search projects by name, code, or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        )}

        {/* Filter Tabs and Action Buttons */}
        <div className="controls-row">
          <div className="filter-group">
            <button 
              className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
              onClick={() => setFilterStatus('all')}
            >
              All ({stats.total})
            </button>
            <button 
              className={`filter-btn ${filterStatus === 'active' ? 'active' : ''}`}
              onClick={() => setFilterStatus('active')}
            >
              Active ({stats.active})
            </button>
            <button 
              className={`filter-btn ${filterStatus === 'running' ? 'active' : ''}`}
              onClick={() => setFilterStatus('running')}
            >
              Running ({stats.running})
            </button>
            <button 
              className={`filter-btn ${filterStatus === 'completed' ? 'active' : ''}`}
              onClick={() => setFilterStatus('completed')}
            >
              Completed ({stats.completed})
            </button>
            <button 
              className={`filter-btn ${filterStatus === 'on-hold' ? 'active' : ''}`}
              onClick={() => setFilterStatus('on-hold')}
            >
              On Hold ({stats.onHold})
            </button>
          </div>

          <div className="controls-right">
            <div className="view-toggle">
              <button
                className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Grid View"
              >
                <i className="fas fa-th"></i>
              </button>
              <button
                className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="List View"
              >
                <i className="fas fa-list"></i>
              </button>
            </div>

            <button className="btn-secondary">
              <i className="fas fa-filter"></i> More Filters
            </button>

            <button className="btn-secondary">
              <i className="fas fa-download"></i> Export
            </button>
          </div>
        </div>
      </div>

      {/* Projects Grid/List */}
      <div className={`projects-container ${viewMode}`}>
        {filteredProjects.map(project => (
          viewMode === 'grid' ? (
            // Grid View Card
            <div 
              key={project.id} 
              className="project-card"
              onClick={() => navigate(`/projects/${project.id}`)}
              style={{ cursor: 'pointer' }}
            >
              <div className="project-card-header">
                <div className="project-title-section">
                  <h3 className="project-name">{project.name}</h3>
                  <span className="project-code">{project.code}</span>
                </div>
                <div className="header-actions">
                  {getStatusBadge(project.status)}
                  <button 
                    className="edit-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditModal(project);
                    }}
                    title="Edit Project"
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                </div>
              </div>

              <div className="project-meta">
                <div className="meta-item meta-with-action">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>{project.location}</span>
                  <button 
                    className="map-icon-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedProject(project);
                      setShowMapModal(true);
                    }}
                    title="View on map"
                  >
                    <i className="fas fa-external-link-alt"></i>
                  </button>
                </div>
                {project.projectManager && (
                  <div className="meta-item">
                    <i className="fas fa-user-tie"></i>
                    <span>{project.projectManager}</span>
                  </div>
                )}
              </div>

              {/* project.progress (a manually-set, non-compliance field)
                  used to be shown here as "Overall Progress" - replaced by
                  the real Compliance Score below, which is what this page
                  should actually surface. */}
              <div className="project-compliance">
                <h4 className="compliance-title">Compliance Score</h4>
                {(() => {
                  const score = scoresByProject[project.id];
                  if (!score) {
                    return <div className="compliance-item"><span className="compliance-label">Loading...</span></div>;
                  }
                  return (
                    <div className="compliance-item">
                      <span className={`compliance-score ${RAG_CLASS[score.band] || 'success'}`}>
                        {Math.round(score.score * 100)}%
                      </span>
                      <span className="compliance-label">
                        {score.band} · {score.open} open finding{score.open === 1 ? '' : 's'}
                        {score.hasUnresolvedCritical ? ' · unresolved critical' : ''}
                      </span>
                    </div>
                  );
                })()}
              </div>

              <div className="project-footer">
                {trackWorkers && (
                  <div className="footer-item">
                    <i className="fas fa-users"></i>
                    <span>{project.workers ?? '—'} workers</span>
                  </div>
                )}
                <div className="footer-item">
                  <i className="fas fa-exclamation-triangle"></i>
                  <span>{project.risks.high + project.risks.medium} risks</span>
                </div>
                <div className="footer-item">
                  <i className="fas fa-calendar-check"></i>
                  <span>{new Date(project.lastInspection).toLocaleDateString()}</span>
                </div>
              </div>

              <div className="project-standards">
                {project.standards.slice(0, 4).map((standard, idx) => (
                  <span key={idx} className="standard-badge">{standard}</span>
                ))}
                {project.standards.length > 4 && (
                  <span className="standard-badge more">+{project.standards.length - 4}</span>
                )}
              </div>
            </div>
          ) : (
            // List View Row
            <Link to={`/projects/${project.id}`} key={project.id} className="project-list-item">
              <div className="list-main">
                <div className="list-title-section">
                  <h3 className="project-name">{project.name}</h3>
                  <span className="project-code">{project.code}</span>
                  <span 
                    className="project-location clickable"
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedProject(project);
                      setShowMapModal(true);
                    }}
                    title="Click to view on map"
                  >
                    <i className="fas fa-map-marker-alt"></i> {project.location}
                    <i className="fas fa-external-link-alt" style={{ fontSize: '9px', marginLeft: '4px', opacity: 0.6 }}></i>
                  </span>
                </div>
                {getStatusBadge(project.status)}
              </div>

              <div className="list-compliance">
                {(() => {
                  const score = scoresByProject[project.id];
                  if (!score) {
                    return <div className="compliance-mini"><span className="mini-label">Loading...</span></div>;
                  }
                  return (
                    <div className="compliance-mini">
                      <span className="mini-label">Compliance</span>
                      <span className={`mini-score ${RAG_CLASS[score.band] || 'success'}`}>
                        {Math.round(score.score * 100)}%
                      </span>
                    </div>
                  );
                })()}
              </div>

              <div className="list-meta">
                <div className="list-meta-item">
                  <i className="fas fa-user-tie"></i>
                  <span>{project.projectManager}</span>
                </div>
                {trackWorkers && (
                  <div className="list-meta-item">
                    <i className="fas fa-users"></i>
                    <span>{project.workers ?? '—'}</span>
                  </div>
                )}
                <div className="list-meta-item">
                  <i className="fas fa-exclamation-triangle"></i>
                  <span>{project.risks.high}H / {project.risks.medium}M</span>
                </div>
              </div>

              <div className="list-actions">
                <button 
                  className="action-btn" 
                  title="View on Map"
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedProject(project);
                    setShowMapModal(true);
                  }}
                >
                  <i className="fas fa-map-marked-alt"></i>
                </button>
                <button className="action-btn" title="View Details">
                  <i className="fas fa-eye"></i>
                </button>
                <button 
                  className="action-btn" 
                  title="Edit Project"
                  onClick={(e) => {
                    e.preventDefault();
                    openEditModal(project);
                  }}
                >
                  <i className="fas fa-edit"></i>
                </button>
                <button className="action-btn" title="Generate Report">
                  <i className="fas fa-file-download"></i>
                </button>
              </div>
            </Link>
          )
        ))}
      </div>

      {filteredProjects.length === 0 && (
        <div className="empty-state">
          <i className="fas fa-folder-open"></i>
          <h3>No projects found</h3>
          <p>Try adjusting your search or filters</p>
        </div>
      )}

      {/* Create Project Modal */}
      {showCreateModal && (
        <CreateProjectModal 
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateProject}
          saving={creating}
          orgDefaultTrackingCategories={organisation?.trackingCategories}
          orgHasWorkersOnSite={trackWorkers}
        />
      )}

      {/* Edit Project Modal */}
      {showEditModal && editingProject && (
        <CreateProjectModal 
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingProject(null);
          }}
          onSave={handleUpdateProject}
          saving={updating}
          editMode={true}
          orgHasWorkersOnSite={trackWorkers}
          initialData={{
            name: editingProject.name || '',
            code: editingProject.code || '',
            description: editingProject.description || '',
            projectStage: editingProject.projectStage || 'existing',
            location: editingProject.location || '',
            latitude: editingProject.latitude || '',
            longitude: editingProject.longitude || '',
            country: editingProject.countryCode || 'UG',
            projectType: editingProject.projectType || '',
            projectTypeOther: editingProject.projectTypeOther || '',
            startDate: editingProject.startDate || '',
            endDate: editingProject.endDate || '',
            contractor: editingProject.contractor || '',
            projectManager: editingProject.projectManager || '',
            trackingCategories: editingProject.trackingCategories || organisation?.trackingCategories || DEFAULT_TRACKING_CATEGORIES,
            customCategories: editingProject.customCategories || [],
            numberOfWorkers: editingProject.numberOfWorkers ?? '',
            locations: editingProject.locations || []
          }}
        />
      )}

      {/* Map Modal */}
      {showMapModal && selectedProject && (
        <MapModal
          isOpen={showMapModal}
          project={selectedProject}
          showWorkers={trackWorkers}
          onClose={() => {
            setShowMapModal(false);
            setSelectedProject(null);
          }}
        />
      )}
    </div>
  );
};

// Map Modal Component
const MapModal = ({ isOpen, project, onClose, showWorkers = true }) => {
  const [copySuccess, setCopySuccess] = useState(false);
  
  if (!isOpen) return null;

  const handleCopyCoordinates = () => {
    navigator.clipboard.writeText(`${project.latitude}, ${project.longitude}`);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  // Use Google Maps embed instead
  const mapUrl = `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${project.latitude},${project.longitude}&zoom=15`;

  return (
    <div className="map-modal-overlay" onClick={onClose}>
      <div className="map-modal" onClick={(e) => e.stopPropagation()}>
        <div className="map-modal-header">
          <div>
            <h2>
              <i className="fas fa-map-marked-alt"></i> {project.name}
            </h2>
            <p className="map-subtitle">{project.location}</p>
          </div>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="map-modal-content">
          <div className="map-info-cards">
            <div className="map-info-card">
              <div className="info-label">GPS Coordinates</div>
              <div className="info-value">
                <i className="fas fa-location-arrow"></i>
                {project.latitude.toFixed(4)}°, {project.longitude.toFixed(4)}°
              </div>
            </div>

            <div className="map-info-card">
              <div className="info-label">Project Code</div>
              <div className="info-value">{project.code}</div>
            </div>

            <div className="map-info-card">
              <div className="info-label">Status</div>
              <div className="info-value">
                <span className={`status-badge status-${project.status === 'active' ? 'success' : project.status === 'completed' ? 'completed' : project.status === 'planning' ? 'info' : 'warning'}`}>
                  {project.status}
                </span>
              </div>
            </div>

            {showWorkers && (
              <div className="map-info-card">
                <div className="info-label">Workers on Site</div>
                <div className="info-value">
                  <i className="fas fa-users"></i> {project.workers ?? '—'}
                </div>
              </div>
            )}
          </div>

          <div className="map-container">
            <iframe
              width="100%"
              height="500"
              frameBorder="0"
              style={{ border: 0, borderRadius: '8px' }}
              referrerPolicy="no-referrer-when-downgrade"
              src={mapUrl}
              allowFullScreen
              title="Project Location Map"
            ></iframe>
          </div>

          <div className="map-actions">
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${project.latitude},${project.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary-green"
            >
              <i className="fas fa-external-link-alt"></i> Open in Google Maps
            </a>
            <button
              className="btn-secondary"
              onClick={handleCopyCoordinates}
            >
              <i className={`fas ${copySuccess ? 'fa-check' : 'fa-copy'}`}></i> 
              {copySuccess ? 'Copied!' : 'Copy Coordinates'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Create Project Modal Component
const CreateProjectModal = ({
  isOpen, onClose, onSave, saving = false, editMode = false, initialData = null, orgDefaultTrackingCategories = null,
  orgHasWorkersOnSite = true,
}) => {
  const [customCategoryInput, setCustomCategoryInput] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const defaultFormData = {
    name: '',
    code: '',
    description: '',
    projectStage: 'existing', // 'new' or 'existing'
    location: '',
    latitude: '',
    longitude: '',
    country: 'UG',
    projectType: '',
    projectTypeOther: '',
    startDate: '',
    endDate: '',
    contractor: '',
    projectManager: '',
    // New projects inherit what the org selected during onboarding (Settings
    // > Compliance Tracking) when it's set; otherwise fall back to the
    // original sensible defaults. Still fully editable per project below.
    trackingCategories: orgDefaultTrackingCategories || DEFAULT_TRACKING_CATEGORIES,
    customCategories: [],
    numberOfWorkers: '',
    // Named sub-sites/areas within this project (e.g. "Gate 1", "Warehouse")
    // - optional. When set, Inspections pick one of these instead of typing
    // free text; when left empty, Inspections skip asking about location at
    // all since there's nothing to distinguish.
    locations: []
  };
  const [formData, setFormData] = useState(initialData || defaultFormData);

  // Categories already selected when the modal opened (org defaults for a
  // new project, or the project's own choices when editing) are pinned to
  // the top of the grid so they're immediately visible - computed once on
  // mount rather than re-sorted on every toggle, so cards don't jump around
  // as the user clicks through them.
  const [orderedTrackingCategories] = useState(() => {
    const initialSelection = (initialData || defaultFormData).trackingCategories || {};
    return [...TRACKING_CATEGORIES].sort(
      (a, b) => (initialSelection[b.id] ? 1 : 0) - (initialSelection[a.id] ? 1 : 0)
    );
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCategoryToggle = (category) => {
    setFormData(prev => ({
      ...prev,
      trackingCategories: {
        ...prev.trackingCategories,
        [category]: !prev.trackingCategories[category]
      }
    }));
  };

  const [showValidation, setShowValidation] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate at least one category is selected
    const selectedCount = Object.values(formData.trackingCategories).filter(Boolean).length;
    if (selectedCount === 0) {
      setShowValidation(true);
      return;
    }
    
    onSave(formData);
  };

  if (!isOpen) return null;

  const projectTypes = [
    'Advocacy & Policy Program',
    'Agricultural Cooperative',
    'Agricultural Development',
    'Airport',
    'Bridge & Infrastructure',
    'Building Construction',
    'Civil Society Organisation (CSO) Program',
    'Community-Based Organisation (CBO) Program',
    'Conservation & Wildlife Management',
    'Education & School Facility',
    'Faith-Based Organisation Program',
    'Financial Services / Banking',
    'Forestry',
    'Fuel Station/Depot',
    'Healthcare Facility',
    'Hospitality & Tourism',
    'Humanitarian & Emergency Response',
    'Hydropower & Dam',
    'ICT / Technology Hub',
    'Industrial Park',
    'Legal Aid & Human Rights',
    'Manufacturing Plant/Factory',
    'Media & Communications',
    'Microfinance & Financial Inclusion',
    'Mining & Quarrying',
    'Non-Governmental Organisation (NGO) Program',
    'Oil & Gas',
    'Port & Maritime',
    'Power Distribution',
    'Public Health Program',
    'Railway',
    'Real Estate Development',
    'Refugee & Displaced Persons Support',
    'Renewable Energy',
    'Research & Academic Institution',
    'Road Construction',
    'Social Welfare & Protection Services',
    'Telecommunications',
    'Vocational & Skills Training',
    'Waste Management & Recycling',
    'Water & Sanitation',
    'Youth & Women Empowerment Program',
    'Other (Please specify)'
  ];

  const allCountries = [
    { code: 'AF', name: 'Afghanistan', flag: '🇦🇫' },
    { code: 'AL', name: 'Albania', flag: '🇦🇱' },
    { code: 'DZ', name: 'Algeria', flag: '🇩🇿' },
    { code: 'AD', name: 'Andorra', flag: '🇦🇩' },
    { code: 'AO', name: 'Angola', flag: '🇦🇴' },
    { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
    { code: 'AM', name: 'Armenia', flag: '🇦🇲' },
    { code: 'AU', name: 'Australia', flag: '🇦🇺' },
    { code: 'AT', name: 'Austria', flag: '🇦🇹' },
    { code: 'AZ', name: 'Azerbaijan', flag: '🇦🇿' },
    { code: 'BS', name: 'Bahamas', flag: '🇧🇸' },
    { code: 'BH', name: 'Bahrain', flag: '🇧🇭' },
    { code: 'BD', name: 'Bangladesh', flag: '🇧🇩' },
    { code: 'BB', name: 'Barbados', flag: '🇧🇧' },
    { code: 'BY', name: 'Belarus', flag: '🇧🇾' },
    { code: 'BE', name: 'Belgium', flag: '🇧🇪' },
    { code: 'BZ', name: 'Belize', flag: '🇧🇿' },
    { code: 'BJ', name: 'Benin', flag: '🇧🇯' },
    { code: 'BT', name: 'Bhutan', flag: '🇧🇹' },
    { code: 'BO', name: 'Bolivia', flag: '🇧🇴' },
    { code: 'BA', name: 'Bosnia and Herzegovina', flag: '🇧🇦' },
    { code: 'BW', name: 'Botswana', flag: '🇧🇼' },
    { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
    { code: 'BN', name: 'Brunei', flag: '🇧🇳' },
    { code: 'BG', name: 'Bulgaria', flag: '🇧🇬' },
    { code: 'BF', name: 'Burkina Faso', flag: '🇧🇫' },
    { code: 'BI', name: 'Burundi', flag: '🇧🇮' },
    { code: 'KH', name: 'Cambodia', flag: '🇰🇭' },
    { code: 'CM', name: 'Cameroon', flag: '🇨🇲' },
    { code: 'CA', name: 'Canada', flag: '🇨🇦' },
    { code: 'CV', name: 'Cape Verde', flag: '🇨🇻' },
    { code: 'CF', name: 'Central African Republic', flag: '🇨🇫' },
    { code: 'TD', name: 'Chad', flag: '🇹🇩' },
    { code: 'CL', name: 'Chile', flag: '🇨🇱' },
    { code: 'CN', name: 'China', flag: '🇨🇳' },
    { code: 'CO', name: 'Colombia', flag: '🇨🇴' },
    { code: 'KM', name: 'Comoros', flag: '🇰🇲' },
    { code: 'CG', name: 'Congo', flag: '🇨🇬' },
    { code: 'CD', name: 'DR Congo', flag: '🇨🇩' },
    { code: 'CR', name: 'Costa Rica', flag: '🇨🇷' },
    { code: 'HR', name: 'Croatia', flag: '🇭🇷' },
    { code: 'CU', name: 'Cuba', flag: '🇨🇺' },
    { code: 'CY', name: 'Cyprus', flag: '🇨🇾' },
    { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿' },
    { code: 'CI', name: 'Côte d\'Ivoire', flag: '🇨🇮' },
    { code: 'DK', name: 'Denmark', flag: '🇩🇰' },
    { code: 'DJ', name: 'Djibouti', flag: '🇩🇯' },
    { code: 'DM', name: 'Dominica', flag: '🇩🇲' },
    { code: 'DO', name: 'Dominican Republic', flag: '🇩🇴' },
    { code: 'EC', name: 'Ecuador', flag: '🇪🇨' },
    { code: 'EG', name: 'Egypt', flag: '🇪🇬' },
    { code: 'SV', name: 'El Salvador', flag: '🇸🇻' },
    { code: 'GQ', name: 'Equatorial Guinea', flag: '🇬🇶' },
    { code: 'ER', name: 'Eritrea', flag: '🇪🇷' },
    { code: 'EE', name: 'Estonia', flag: '🇪🇪' },
    { code: 'SZ', name: 'Eswatini', flag: '🇸🇿' },
    { code: 'ET', name: 'Ethiopia', flag: '🇪🇹' },
    { code: 'FJ', name: 'Fiji', flag: '🇫🇯' },
    { code: 'FI', name: 'Finland', flag: '🇫🇮' },
    { code: 'FR', name: 'France', flag: '🇫🇷' },
    { code: 'GA', name: 'Gabon', flag: '🇬🇦' },
    { code: 'GM', name: 'Gambia', flag: '🇬🇲' },
    { code: 'GE', name: 'Georgia', flag: '🇬🇪' },
    { code: 'DE', name: 'Germany', flag: '🇩🇪' },
    { code: 'GH', name: 'Ghana', flag: '🇬🇭' },
    { code: 'GR', name: 'Greece', flag: '🇬🇷' },
    { code: 'GD', name: 'Grenada', flag: '🇬🇩' },
    { code: 'GT', name: 'Guatemala', flag: '🇬🇹' },
    { code: 'GN', name: 'Guinea', flag: '🇬🇳' },
    { code: 'GW', name: 'Guinea-Bissau', flag: '🇬🇼' },
    { code: 'GY', name: 'Guyana', flag: '🇬🇾' },
    { code: 'HT', name: 'Haiti', flag: '🇭🇹' },
    { code: 'HN', name: 'Honduras', flag: '🇭🇳' },
    { code: 'HU', name: 'Hungary', flag: '🇭🇺' },
    { code: 'IS', name: 'Iceland', flag: '🇮🇸' },
    { code: 'IN', name: 'India', flag: '🇮🇳' },
    { code: 'ID', name: 'Indonesia', flag: '🇮🇩' },
    { code: 'IR', name: 'Iran', flag: '🇮🇷' },
    { code: 'IQ', name: 'Iraq', flag: '🇮🇶' },
    { code: 'IE', name: 'Ireland', flag: '🇮🇪' },
    { code: 'IL', name: 'Israel', flag: '🇮🇱' },
    { code: 'IT', name: 'Italy', flag: '🇮🇹' },
    { code: 'JM', name: 'Jamaica', flag: '🇯🇲' },
    { code: 'JP', name: 'Japan', flag: '🇯🇵' },
    { code: 'JO', name: 'Jordan', flag: '🇯🇴' },
    { code: 'KZ', name: 'Kazakhstan', flag: '🇰🇿' },
    { code: 'KE', name: 'Kenya', flag: '🇰🇪' },
    { code: 'KI', name: 'Kiribati', flag: '🇰🇮' },
    { code: 'KP', name: 'North Korea', flag: '🇰🇵' },
    { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
    { code: 'KW', name: 'Kuwait', flag: '🇰🇼' },
    { code: 'KG', name: 'Kyrgyzstan', flag: '🇰🇬' },
    { code: 'LA', name: 'Laos', flag: '🇱🇦' },
    { code: 'LV', name: 'Latvia', flag: '🇱🇻' },
    { code: 'LB', name: 'Lebanon', flag: '🇱🇧' },
    { code: 'LS', name: 'Lesotho', flag: '🇱🇸' },
    { code: 'LR', name: 'Liberia', flag: '🇱🇷' },
    { code: 'LY', name: 'Libya', flag: '🇱🇾' },
    { code: 'LI', name: 'Liechtenstein', flag: '🇱🇮' },
    { code: 'LT', name: 'Lithuania', flag: '🇱🇹' },
    { code: 'LU', name: 'Luxembourg', flag: '🇱🇺' },
    { code: 'MG', name: 'Madagascar', flag: '🇲🇬' },
    { code: 'MW', name: 'Malawi', flag: '🇲🇼' },
    { code: 'MY', name: 'Malaysia', flag: '🇲🇾' },
    { code: 'MV', name: 'Maldives', flag: '🇲🇻' },
    { code: 'ML', name: 'Mali', flag: '🇲🇱' },
    { code: 'MT', name: 'Malta', flag: '🇲🇹' },
    { code: 'MH', name: 'Marshall Islands', flag: '🇲🇭' },
    { code: 'MR', name: 'Mauritania', flag: '🇲🇷' },
    { code: 'MU', name: 'Mauritius', flag: '🇲🇺' },
    { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
    { code: 'FM', name: 'Micronesia', flag: '🇫🇲' },
    { code: 'MD', name: 'Moldova', flag: '🇲🇩' },
    { code: 'MC', name: 'Monaco', flag: '🇲🇨' },
    { code: 'MN', name: 'Mongolia', flag: '🇲🇳' },
    { code: 'ME', name: 'Montenegro', flag: '🇲🇪' },
    { code: 'MA', name: 'Morocco', flag: '🇲🇦' },
    { code: 'MZ', name: 'Mozambique', flag: '🇲🇿' },
    { code: 'MM', name: 'Myanmar', flag: '🇲🇲' },
    { code: 'NA', name: 'Namibia', flag: '🇳🇦' },
    { code: 'NR', name: 'Nauru', flag: '🇳🇷' },
    { code: 'NP', name: 'Nepal', flag: '🇳🇵' },
    { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
    { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
    { code: 'NI', name: 'Nicaragua', flag: '🇳🇮' },
    { code: 'NE', name: 'Niger', flag: '🇳🇪' },
    { code: 'NG', name: 'Nigeria', flag: '🇳🇬' },
    { code: 'MK', name: 'North Macedonia', flag: '🇲🇰' },
    { code: 'NO', name: 'Norway', flag: '🇳🇴' },
    { code: 'OM', name: 'Oman', flag: '🇴🇲' },
    { code: 'PK', name: 'Pakistan', flag: '🇵🇰' },
    { code: 'PW', name: 'Palau', flag: '🇵🇼' },
    { code: 'PA', name: 'Panama', flag: '🇵🇦' },
    { code: 'PG', name: 'Papua New Guinea', flag: '🇵🇬' },
    { code: 'PY', name: 'Paraguay', flag: '🇵🇾' },
    { code: 'PE', name: 'Peru', flag: '🇵🇪' },
    { code: 'PH', name: 'Philippines', flag: '🇵🇭' },
    { code: 'PL', name: 'Poland', flag: '🇵🇱' },
    { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
    { code: 'QA', name: 'Qatar', flag: '🇶🇦' },
    { code: 'RO', name: 'Romania', flag: '🇷🇴' },
    { code: 'RU', name: 'Russia', flag: '🇷🇺' },
    { code: 'RW', name: 'Rwanda', flag: '🇷🇼' },
    { code: 'KN', name: 'Saint Kitts and Nevis', flag: '🇰🇳' },
    { code: 'LC', name: 'Saint Lucia', flag: '🇱🇨' },
    { code: 'VC', name: 'Saint Vincent and the Grenadines', flag: '🇻🇨' },
    { code: 'WS', name: 'Samoa', flag: '🇼🇸' },
    { code: 'SM', name: 'San Marino', flag: '🇸🇲' },
    { code: 'ST', name: 'São Tomé and Príncipe', flag: '🇸🇹' },
    { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦' },
    { code: 'SN', name: 'Senegal', flag: '🇸🇳' },
    { code: 'RS', name: 'Serbia', flag: '🇷🇸' },
    { code: 'SC', name: 'Seychelles', flag: '🇸🇨' },
    { code: 'SL', name: 'Sierra Leone', flag: '🇸🇱' },
    { code: 'SG', name: 'Singapore', flag: '🇸🇬' },
    { code: 'SK', name: 'Slovakia', flag: '🇸🇰' },
    { code: 'SI', name: 'Slovenia', flag: '🇸🇮' },
    { code: 'SB', name: 'Solomon Islands', flag: '🇸🇧' },
    { code: 'SO', name: 'Somalia', flag: '🇸🇴' },
    { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
    { code: 'SS', name: 'South Sudan', flag: '🇸🇸' },
    { code: 'ES', name: 'Spain', flag: '🇪🇸' },
    { code: 'LK', name: 'Sri Lanka', flag: '🇱🇰' },
    { code: 'SD', name: 'Sudan', flag: '🇸🇩' },
    { code: 'SR', name: 'Suriname', flag: '🇸🇷' },
    { code: 'SE', name: 'Sweden', flag: '🇸🇪' },
    { code: 'CH', name: 'Switzerland', flag: '🇨🇭' },
    { code: 'SY', name: 'Syria', flag: '🇸🇾' },
    { code: 'TJ', name: 'Tajikistan', flag: '🇹🇯' },
    { code: 'TZ', name: 'Tanzania', flag: '🇹🇿' },
    { code: 'TH', name: 'Thailand', flag: '🇹🇭' },
    { code: 'TL', name: 'Timor-Leste', flag: '🇹🇱' },
    { code: 'TG', name: 'Togo', flag: '🇹🇬' },
    { code: 'TO', name: 'Tonga', flag: '🇹🇴' },
    { code: 'TT', name: 'Trinidad and Tobago', flag: '🇹🇹' },
    { code: 'TN', name: 'Tunisia', flag: '🇹🇳' },
    { code: 'TR', name: 'Turkey', flag: '🇹🇷' },
    { code: 'TM', name: 'Turkmenistan', flag: '🇹🇲' },
    { code: 'TV', name: 'Tuvalu', flag: '🇹🇻' },
    { code: 'UG', name: 'Uganda', flag: '🇺🇬' },
    { code: 'UA', name: 'Ukraine', flag: '🇺🇦' },
    { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪' },
    { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
    { code: 'US', name: 'United States', flag: '🇺🇸' },
    { code: 'UY', name: 'Uruguay', flag: '🇺🇾' },
    { code: 'UZ', name: 'Uzbekistan', flag: '🇺🇿' },
    { code: 'VU', name: 'Vanuatu', flag: '🇻🇺' },
    { code: 'VA', name: 'Vatican City', flag: '🇻🇦' },
    { code: 'VE', name: 'Venezuela', flag: '🇻🇪' },
    { code: 'VN', name: 'Vietnam', flag: '🇻🇳' },
    { code: 'YE', name: 'Yemen', flag: '🇾🇪' },
    { code: 'ZM', name: 'Zambia', flag: '🇿🇲' },
    { code: 'ZW', name: 'Zimbabwe', flag: '🇿🇼' }
  ];

  return (
    <>
      {/* Validation Dialog */}
      {showValidation && (
        <div className="validation-dialog-overlay" onClick={() => setShowValidation(false)}>
          <div className="validation-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-icon warning">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <h3 className="dialog-title">Category Required</h3>
            <p className="dialog-message">
              Please select at least one compliance category to track for this project.
            </p>
            <button className="btn-primary" onClick={() => setShowValidation(false)}>
              OK
            </button>
          </div>
        </div>
      )}

      <div className="project-modal-overlay">
        <div className="project-modal">
        <div className="modal-header">
          <h2>
            <i className={`fas ${editMode ? 'fa-edit' : 'fa-folder-plus'}`}></i> {editMode ? 'Edit Project' : 'Create New Project'}
          </h2>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-content">
            {/* Basic Information */}
            <div className="form-section">
              <h3 className="section-title">Basic Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Project Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., Kampala Highway Expansion"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Project Code (Optional)</label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    placeholder="e.g., KHE-2024-001"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Brief project description"
                    rows="3"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Project Stage *</label>
                  <select
                    name="projectStage"
                    value={formData.projectStage}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="existing">Existing/Active (Already operational - compliance monitoring)</option>
                    <option value="new">New Project (Under construction or development)</option>
                  </select>
                </div>

                <div className="form-group full-width">
                  <label>Location *</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="e.g., Kampala, Uganda"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>GPS Latitude</label>
                  <input
                    type="text"
                    name="latitude"
                    value={formData.latitude}
                    onChange={handleInputChange}
                    placeholder="e.g., 0.3476"
                  />
                </div>

                <div className="form-group">
                  <label>GPS Longitude</label>
                  <input
                    type="text"
                    name="longitude"
                    value={formData.longitude}
                    onChange={handleInputChange}
                    placeholder="e.g., 32.5825"
                  />
                </div>

                <div className="form-group">
                  <label>Country *</label>
                  <Select
                    name="country"
                    value={allCountries.find(c => c.code === formData.country)}
                    onChange={(selectedOption) => {
                      setFormData(prev => ({
                        ...prev,
                        country: selectedOption ? selectedOption.code : ''
                      }));
                    }}
                    options={allCountries}
                    getOptionLabel={(option) => `${option.flag} ${option.name}`}
                    getOptionValue={(option) => option.code}
                    placeholder="Search or select a country..."
                    isSearchable={true}
                    noOptionsMessage={() => "No country found. Please check your spelling."}
                    className="country-select-searchable"
                    classNamePrefix="select"
                    required
                  />
                  <small className="form-hint">Type to search countries</small>
                </div>

                <div className="form-group">
                  <label>Project Type *</label>
                  <Select
                    name="projectType"
                    value={projectTypes.find(type => type === formData.projectType) ? { label: formData.projectType, value: formData.projectType } : null}
                    onChange={(selectedOption) => {
                      setFormData(prev => ({
                        ...prev,
                        projectType: selectedOption ? selectedOption.value : ''
                      }));
                    }}
                    options={projectTypes.map(type => ({ label: type, value: type }))}
                    placeholder="Search or select project type..."
                    isSearchable={true}
                    filterOption={(option, inputValue) => {
                      // Always show "Other (Please specify)" option
                      if (option.label === 'Other (Please specify)') {
                        return true;
                      }
                      // Filter other options normally
                      return option.label.toLowerCase().includes(inputValue.toLowerCase());
                    }}
                    noOptionsMessage={() => 'No exact match found. You can select "Other (Please specify)" below.'}
                    className="project-type-select-searchable"
                    classNamePrefix="select"
                    required
                  />
                  <small className="form-hint">Type to search project types</small>
                </div>

                {formData.projectType === 'Other (Please specify)' && (
                  <div className="form-group">
                    <label>Specify Project Type *</label>
                    <input
                      type="text"
                      name="projectTypeOther"
                      value={formData.projectTypeOther}
                      onChange={handleInputChange}
                      placeholder="e.g., Waste Management Facility"
                      required
                    />
                  </div>
                )}

                {/* Show dates and project team only for new projects */}
                {formData.projectStage === 'new' && (
                  <>
                    <div className="form-group">
                      <label>Start Date *</label>
                      <DatePickerField
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleInputChange}
                        placeholder="Select start date"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Expected End Date</label>
                      <DatePickerField
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleInputChange}
                        minDate={formData.startDate}
                        placeholder="Select end date"
                      />
                    </div>

                    <div className="form-group">
                      <label>Contractor *</label>
                      <input
                        type="text"
                        name="contractor"
                        value={formData.contractor}
                        onChange={handleInputChange}
                        placeholder="e.g., ABC Construction Ltd"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Project Manager</label>
                      <input
                        type="text"
                        name="projectManager"
                        value={formData.projectManager}
                        onChange={handleInputChange}
                        placeholder="e.g., Sarah Nambi"
                      />
                    </div>
                  </>
                )}

                {/* For existing projects, show operation start date */}
                {formData.projectStage === 'existing' && (
                  <div className="form-group">
                    <label>Operational Since (Optional)</label>
                    <DatePickerField
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                      placeholder="Select date"
                    />
                  </div>
                )}

                {orgHasWorkersOnSite && (
                  <div className="form-group">
                    <label>Number of Workers (Optional)</label>
                    <input
                      type="number"
                      min="0"
                      name="numberOfWorkers"
                      value={formData.numberOfWorkers}
                      onChange={handleInputChange}
                      placeholder="e.g., 120"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Sub-Locations - optional. If this project spans multiple
                distinct areas, list them here so Inspections can pick one
                instead of typing free text every time; leave empty for a
                single-location project and Inspections won't ask at all. */}
            <div className="form-section">
              <h3 className="section-title">
                <i className="fas fa-map-marker-alt"></i> Sub-Locations (Optional)
              </h3>
              <p className="section-description">
                Does this project span multiple distinct areas or sites (e.g. "Gate 1", "Warehouse", "Site A")?
                List them here and inspectors will pick from this list during each inspection instead of typing it
                in free text. Leave empty if this project has just one location - inspectors won't be asked.
              </p>

              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <input
                  type="text"
                  value={locationInput}
                  onChange={(e) => setLocationInput(e.target.value)}
                  placeholder="e.g., Gate 1"
                  style={{ flex: 1, padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px' }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (locationInput.trim() && !formData.locations.includes(locationInput.trim())) {
                        setFormData(prev => ({ ...prev, locations: [...prev.locations, locationInput.trim()] }));
                        setLocationInput('');
                      }
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (locationInput.trim() && !formData.locations.includes(locationInput.trim())) {
                      setFormData(prev => ({ ...prev, locations: [...prev.locations, locationInput.trim()] }));
                      setLocationInput('');
                    }
                  }}
                  style={{ padding: '10px 20px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
                >
                  <i className="fas fa-plus"></i> Add
                </button>
              </div>

              {formData.locations.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {formData.locations.map((loc, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', fontSize: '13px', color: '#15803d' }}>
                      <i className="fas fa-map-pin"></i>
                      <span>{loc}</span>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, locations: prev.locations.filter((_, i) => i !== index) }))}
                        style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '0 4px', fontSize: '14px' }}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Compliance Categories */}
            <div className="form-section">
              <h3 className="section-title">
                <i className="fas fa-check-square"></i> Compliance Categories
              </h3>
              <p className="section-description">
                Select the compliance categories you want to track for this project. <strong>At least one category is required.</strong>
              </p>
              
              <div className="categories-grid">
                {orderedTrackingCategories.map(category => (
                  <div 
                    key={category.id}
                    className={`category-card ${formData.trackingCategories[category.id] ? 'selected' : ''}`}
                    onClick={() => handleCategoryToggle(category.id)}
                  >
                    <div className="category-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.trackingCategories[category.id]}
                        onChange={() => handleCategoryToggle(category.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="checkmark"></span>
                    </div>
                    <div 
                      className="category-icon" 
                      style={{ backgroundColor: `${category.color}15`, color: category.color }}
                    >
                      <i className={`fas ${category.icon}`}></i>
                    </div>
                    <div className="category-label">{category.label}</div>
                  </div>
                ))}
              </div>

              {/* Custom Categories Section */}
              <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
                <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#374151', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <i className="fas fa-plus-circle" style={{ color: '#16a34a' }}></i>
                  Add Custom Categories (Optional)
                </h4>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                  Add organization-specific categories like "Supply Chain Ethics", "Data Privacy", etc.
                </p>
                
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                  <input
                    type="text"
                    value={customCategoryInput}
                    onChange={(e) => setCustomCategoryInput(e.target.value)}
                    placeholder="e.g., Supply Chain Ethics"
                    style={{ flex: 1, padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px' }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (customCategoryInput.trim() && !formData.customCategories.includes(customCategoryInput.trim())) {
                          setFormData(prev => ({ ...prev, customCategories: [...prev.customCategories, customCategoryInput.trim()] }));
                          setCustomCategoryInput('');
                        }
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (customCategoryInput.trim() && !formData.customCategories.includes(customCategoryInput.trim())) {
                        setFormData(prev => ({ ...prev, customCategories: [...prev.customCategories, customCategoryInput.trim()] }));
                        setCustomCategoryInput('');
                      }
                    }}
                    style={{ padding: '10px 20px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
                  >
                    <i className="fas fa-plus"></i> Add
                  </button>
                </div>

                {formData.customCategories.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {formData.customCategories.map((category, index) => (
                      <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', fontSize: '13px', color: '#15803d' }}>
                        <i className="fas fa-tag"></i>
                        <span>{category}</span>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, customCategories: prev.customCategories.filter((_, i) => i !== index) }))}
                          style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '0 4px', fontSize: '14px' }}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? (
                <><i className="fas fa-spinner fa-spin"></i> {editMode ? 'Updating...' : 'Creating...'}</>
              ) : (
                editMode ? 'Update Project' : 'Create Project'
              )}
            </button>
          </div>
        </form>
        </div>
      </div>
    </>
  );
};

export default Projects;
